import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Grid,
  CircularProgress,
  TextField,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useMediaQuery, useTheme } from "@mui/material";

import { useSnackbar } from "notistack";

import Logo from "@assets/images/logo.png";

import { API } from "@services/API";

export default function Login({ ...props }) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const history = useHistory();
  const location = useLocation();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [messageDisplayed, setMessageDisplayed] = useState(false);

  useEffect(() => {
    localStorage.clear();

    return () => {
      setMessageDisplayed(false);
    };
  }, []);

  useEffect(() => {
    const messageFromState = location.state?.message;

    if (messageFromState && !messageDisplayed) {
      showSnackBar("error", "Session Expired", messageFromState);

      setMessageDisplayed(true);

      history.push({
        pathname: "/login",
        state: undefined,
      });
    }
  }, [location.state, messageDisplayed]);

  const showSnackBar = (type, title, subtitle) => {
    const options = { variant: type };

    enqueueSnackbar(
      <div style={{ display: "inline-grid" }}>
        <Typography data-test={"snack-title"} variant="h6">
          {title}
        </Typography>

        <Typography data-test={"snack-text"} variant="subtitle1">
          {subtitle}
        </Typography>
      </div>,
      options
    );
  };

  const isNullOrEmpty = (value) => {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === "string" && value.trim().toLowerCase() === "null") {
      return true;
    }

    if (Array.isArray(value) && value.length === 0) {
      return true;
    }

    if (typeof value === "string" && value.trim() === "") {
      return true;
    }

    if (typeof value === "number" && isNaN(value)) {
      return true;
    }

    return false;
  };

  const isValidEmail = (email) => {
    if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
      return true;
    } else {
      return false;
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = (event) => {
    event.preventDefault();

    if (isNullOrEmpty(email)) {
      showSnackBar("error", "Failure", "Email can't be empty.");
    } else if (!isValidEmail(email)) {
      showSnackBar("error", "Failure", "Invalid email address.");
    } else if (isNullOrEmpty(password)) {
      showSnackBar("error", "Failure", "Password can't be empty.");
    } else {
      setLoading(true);

      API.post(`/user/login`, {
        UserName: email.trim(),
        Password: password.trim(),
      })
        .then((response) => {
          if (response.data.token && response.data.user_id) {
            localStorage.setItem("covid19_token", response.data.token);
            localStorage.setItem("user_id", response.data.user_id);
            localStorage.setItem("user_level", response.data.level);
            localStorage.setItem("user_facility_id", response.data.hospital_id);
            localStorage.setItem("email", email);
            localStorage.setItem("hospital_id", response.data.hospital_id);
            localStorage.setItem("mfa_secret", response.data.mfa_secret);
            localStorage.setItem("hospital_name", response.data.facility);
            localStorage.setItem(
              "full_name",
              response.data.firstname + " " + response.data.surname
            );

            if (response.data.level >= 8) {
              setLoading(false);
              showSnackBar(
                "error",
                "Access Denied",
                "Sorry, you do not have the required permissions to access this feature or resource."
              );

              localStorage.clear();
              history.push("/login");
            } else {
              history.push("/mfa");
            }
          } else if (
            response.data.message === "User needs to verify account."
          ) {
            localStorage.setItem("email", email);
            history.push("/verify");
          } else {
            setLoading(false);
            showSnackBar("error", "Failure", "Wrong email or password.");
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            localStorage.setItem("email", email);
            history.push("/mfa");
          } else if (error.response && error.response.status === 401) {
            localStorage.setItem("email", email);
            history.push("/verify");
          } else {
            setLoading(false);
            showSnackBar("error", "Failure", "Internal server error.");
          }
        });
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "85vh",
      }}
    >
      <Grid
        container
        spacing={2}
        style={{ width: isSmallScreen ? "90%" : "500px" }}
      >
        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <img src={Logo} alt="logo" height="100px" />
        </Grid>

        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <Typography
            data-test={"login-header"}
            variant="h3"
            color={"primary"}
            style={{ fontWeight: "bold" }}
          >
            Administration Portal
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            data-test={"login-email"}
            variant="outlined"
            id="email"
            label="Email"
            type="email"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            autoFocus={true}
            required={true}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            data-test={"login-password"}
            variant="outlined"
            id="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            required={true}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography data-test={"login-forgot-password"}>
            Forgot your password ?{" "}
            <a href="/forgot" data-test={"login-reset-link"}>
              Click here to reset it
            </a>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Button
            data-test={"login-login-btn"}
            variant="contained"
            type={"submit"}
            size={"large"}
            loading={loading}
            disabled={loading}
            onClick={handleLogin}
            fullWidth
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Log In"
            )}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
}
